import React, { useEffect, useState } from 'react';
import AdminService from '../../services/AdminService';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography
} from '@material-ui/core';
import MaterialTable from 'material-table';
import TableIcons from '../molecules/TableIcons';
import moment from 'moment';
import EditIcon from '@material-ui/icons/Edit';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { SemiFlexibleMonthSummary } from './SemiFlexibleMonthSummary';

const isPastBudgetMonth = (budgetMonth) => {
    const budgetStart = moment(budgetMonth).utc().startOf('month');
    const currentStart = moment.utc().startOf('month');
    return budgetStart.isBefore(currentStart);
};

export const SemiFlexibleBudgetsListModal = ({ company, open, setOpen }) => {
    const [budgets, setBudgets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedBudget, setSelectedBudget] = useState(null);
    const [cityRows, setCityRows] = useState([]);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState(0);

    const loadBudgets = async () => {
        try {
            setLoading(true);
            const res = await AdminService.getCompanySemiFlexibleBudgets(company.id);
            const items = (res?.data || []).sort(
                (a, b) => new Date(b.budget_month) - new Date(a.budget_month)
            );
            setBudgets(items);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const openBudgetEditor = async (budget) => {
        if (isPastBudgetMonth(budget.budget_month)) {
            window.alert('No se pueden modificar presupuestos de meses anteriores.');
            return;
        }

        try {
            setLoading(true);
            const res = await AdminService.getCompanySemiFlexibleBudget(company.id, budget.id);
            const detail = res?.data || budget;
            setSelectedBudget(detail);
            setCityRows((detail.cities || []).map((city) => ({
                id: city.id,
                country_id: city.country_id,
                country_name: city.country_name || `#${city.country_id}`,
                city_name: city.city_name,
                hours: city.hours ?? 0,
                days: city.days ?? 0
            })));
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const saveCityQuotas = async () => {
        if (!selectedBudget?.id) return;
        if (isPastBudgetMonth(selectedBudget.budget_month)) {
            window.alert('No se pueden modificar presupuestos de meses anteriores.');
            return;
        }

        try {
            setSaving(true);
            await AdminService.updateCompanySemiFlexibleBudget(company.id, selectedBudget.id, {
                cities: cityRows.map((city) => ({
                    id: city.id,
                    country_id: city.country_id,
                    city_name: city.city_name,
                    hours: Number(city.hours) || 0,
                    days: Number(city.days) || 0
                }))
            });
            await loadBudgets();
            setSelectedBudget(null);
            setCityRows([]);
        } catch (error) {
            console.log(error);
            const msg = error?.response?.data?.message;
            window.alert(Array.isArray(msg) ? msg.join('\n') : (msg || 'No se pudieron guardar los cupos.'));
        } finally {
            setSaving(false);
        }
    };

    const updateCityField = (cityId, field, value) => {
        setCityRows((prev) => prev.map((row) => (
            row.id === cityId ? { ...row, [field]: value } : row
        )));
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedBudget(null);
        setCityRows([]);
        setTab(0);
    };

    useEffect(() => {
        if (open && company?.id) {
            loadBudgets();
            setTab(0);
            setSelectedBudget(null);
            setCityRows([]);
        }
    }, [company, open]);

    const selectedIsPast = selectedBudget ? isPastBudgetMonth(selectedBudget.budget_month) : false;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth={'lg'}>
            <DialogTitle>
                {selectedBudget ? (
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <IconButton size={'small'} onClick={() => setSelectedBudget(null)}>
                            <ArrowBackIcon />
                        </IconButton>
                        <span>
                            Semi-Flex · <b>{company?.name}</b> · {moment(selectedBudget.budget_month).utc().format('MMMM YYYY')}
                        </span>
                    </Box>
                ) : (
                    <span>Semi-Flex · <b>{company?.name}</b></span>
                )}
            </DialogTitle>
            <DialogContent>
                {selectedBudget ? (
                    <>
                        <Typography variant={'body2'} color={'textSecondary'} style={{ marginBottom: 16 }}>
                            Configurá las horas de sala de reunión y los días de escritorio compartido por ciudad.
                            La empresa define la estructura geográfica; acá se cargan los cupos mensuales.
                        </Typography>
                        {selectedIsPast && (
                            <Typography variant={'body2'} color={'error'} style={{ marginBottom: 12 }}>
                                Este mes ya cerró: no se pueden modificar los cupos.
                            </Typography>
                        )}
                        {cityRows.length === 0 ? (
                            <Typography variant={'body2'} color={'textSecondary'}>
                                Esta empresa aún no creó presupuestos con ciudades para este mes.
                            </Typography>
                        ) : (
                            <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {cityRows.map((city) => (
                                    <Box
                                        key={city.id}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr 140px 140px',
                                            gap: 12,
                                            alignItems: 'center',
                                            padding: '12px 0',
                                            borderBottom: '1px solid rgba(0,0,0,0.08)'
                                        }}
                                    >
                                        <Typography variant={'body2'}><b>{city.country_name}</b></Typography>
                                        <Typography variant={'body2'}>{city.city_name}</Typography>
                                        <TextField
                                            label={'Horas (sala)'}
                                            type={'number'}
                                            size={'small'}
                                            variant={'outlined'}
                                            value={city.hours}
                                            disabled={selectedIsPast}
                                            onChange={(e) => updateCityField(city.id, 'hours', e.target.value)}
                                            inputProps={{ min: 0 }}
                                        />
                                        <TextField
                                            label={'Días (escritorio)'}
                                            type={'number'}
                                            size={'small'}
                                            variant={'outlined'}
                                            value={city.days}
                                            disabled={selectedIsPast}
                                            onChange={(e) => updateCityField(city.id, 'days', e.target.value)}
                                            inputProps={{ min: 0 }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </>
                ) : (
                    <>
                        <Tabs
                            value={tab}
                            onChange={(_, next) => setTab(next)}
                            indicatorColor={'primary'}
                            textColor={'primary'}
                            style={{ marginBottom: 16 }}
                        >
                            <Tab label={'Presupuestos mensuales'} style={{ textTransform: 'none' }} />
                            <Tab label={'Resumen del mes'} style={{ textTransform: 'none' }} />
                        </Tabs>

                        {loading && tab === 0 ? (
                            <CircularProgress style={{ margin: '40px auto', display: 'block' }} />
                        ) : tab === 0 ? (
                            <MaterialTable
                                icons={TableIcons}
                                title={''}
                                options={{ pageSize: 10, pageSizeOptions: [10, 25], sorting: true, thirdSortClick: false }}
                                columns={[
                                    { title: 'ID', render: (b) => `#${b.id}` },
                                    { title: 'Mes', render: (b) => moment(b.budget_month).utc().format('MMMM YYYY') },
                                    { title: 'Horas totales', field: 'total_hours' },
                                    { title: 'Días totales', field: 'total_days' },
                                    { title: 'Horas usadas', field: 'used_hours' },
                                    { title: 'Días usados', field: 'used_days' },
                                    { title: 'Ciudades', render: (b) => (b.cities || []).length },
                                    {
                                        title: 'Acciones',
                                        render: (row) => {
                                            const past = isPastBudgetMonth(row.budget_month);
                                            return (
                                                <Tooltip title={past ? 'Mes cerrado: no se puede editar' : 'Configurar cupos por ciudad'}>
                                                    <span>
                                                        <IconButton
                                                            color={'primary'}
                                                            disabled={past}
                                                            onClick={() => openBudgetEditor(row)}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            );
                                        }
                                    }
                                ]}
                                data={budgets}
                                localization={{
                                    pagination: { labelDisplayedRows: '{from}-{to} de {count}' },
                                    body: { emptyDataSourceMessage: 'No hay presupuestos Semi-Flex para mostrar' }
                                }}
                            />
                        ) : (
                            <SemiFlexibleMonthSummary companyId={company?.id} budgets={budgets} />
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {selectedBudget && cityRows.length > 0 && !selectedIsPast && (
                    <Button
                        onClick={saveCityQuotas}
                        color={'primary'}
                        variant={'contained'}
                        disabled={saving}
                    >
                        {saving ? 'Guardando...' : 'Guardar cupos'}
                    </Button>
                )}
                <Button onClick={handleClose} color={'primary'}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};
